/* eslint-disable */
import React, { useState, useCallback } from "react";
import { useLazyQuery, useMutation } from "@apollo/client";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CloseIcon from "@mui/icons-material/Close";
import BuildIcon from "@mui/icons-material/Build";
import DescriptionIcon from "@mui/icons-material/Description";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import RedoIcon from "@mui/icons-material/Redo";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { toast } from "react-toastify";

import { GENERATE_ROBOTS_TXT, GENERATE_SITEMAP } from "../../gql/queries/ai";
import { GENERATE_REDIRECT_MAP } from "../../gql/mutations/ai";

// --------------- Styled Components ---------------

const CodeBlock = styled("pre")(({ theme }) => ({
  backgroundColor: theme.palette.mode === "dark" ? theme.palette.grey[900] : theme.palette.grey[100],
  padding: theme.spacing(2),
  borderRadius: theme.shape.borderRadius,
  overflowX: "auto",
  fontFamily: "monospace",
  fontSize: "0.8125rem",
  lineHeight: 1.6,
  whiteSpace: "pre-wrap",
  wordBreak: "break-word",
  maxHeight: 400,
  border: `1px solid ${theme.palette.divider}`,
  position: "relative",
}));

const CopyButton = styled(IconButton)(({ theme }) => ({
  position: "absolute",
  top: theme.spacing(1),
  right: theme.spacing(1),
  backgroundColor: theme.palette.mode === "dark" ? theme.palette.grey[800] : theme.palette.grey[200],
  "&:hover": {
    backgroundColor: theme.palette.mode === "dark" ? theme.palette.grey[700] : theme.palette.grey[300],
  },
}));

const TabPanel = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3, 0),
}));

const PlaceholderBox = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  padding: theme.spacing(5),
  textAlign: "center",
  color: theme.palette.text.secondary,
  border: `2px dashed ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
}));

// --------------- Helpers ---------------

const copyToClipboard = text => {
  navigator.clipboard.writeText(text).then(
    () => toast.success("Copied to clipboard"),
    () => toast.error("Failed to copy"),
  );
};

// --------------- Component ---------------

const TechnicalSeoTools = ({ open, onClose, sessionId }) => {
  const [activeTab, setActiveTab] = useState(0);
  const [urlPattern, setUrlPattern] = useState("");
  const [newPattern, setNewPattern] = useState("");

  // ---- Robots.txt Query ----
  const [fetchRobots, { data: robotsData, loading: robotsLoading }] = useLazyQuery(GENERATE_ROBOTS_TXT, { fetchPolicy: "network-only" });

  // ---- Sitemap Query ----
  const [fetchSitemap, { data: sitemapData, loading: sitemapLoading }] = useLazyQuery(GENERATE_SITEMAP, { fetchPolicy: "network-only" });

  // ---- Redirect Map Mutation ----
  const [generateRedirects, { data: redirectData, loading: redirectLoading }] = useMutation(GENERATE_REDIRECT_MAP, {
    onCompleted: () => toast.success("Redirect map generated"),
    onError: err => toast.error(err.message),
  });

  // ---- Handlers ----
  const handleGenerateRobots = useCallback(() => {
    fetchRobots({ variables: { sessionId } });
  }, [fetchRobots, sessionId]);

  const handleGenerateSitemap = useCallback(() => {
    fetchSitemap({ variables: { sessionId } });
  }, [fetchSitemap, sessionId]);

  const handleGenerateRedirects = useCallback(() => {
    generateRedirects({
      variables: {
        data: {
          sessionId,
          urlPattern: urlPattern.trim() || undefined,
          newPattern: newPattern.trim() || undefined,
        },
      },
    });
  }, [generateRedirects, sessionId, urlPattern, newPattern]);

  const handleTabChange = useCallback((_, newVal) => {
    setActiveTab(newVal);
  }, []);

  // ---- Derived data ----
  const robotsResult = robotsData?.generateRobotsTxt || null;
  const sitemapResult = sitemapData?.generateSitemap || null;
  const redirectResult = redirectData?.generateRedirectMap || null;

  // Parse recommendations helper
  const parseList = val => {
    if (!val) return [];
    if (Array.isArray(val)) return val;
    try {
      return JSON.parse(val);
    } catch {
      return [val];
    }
  };

  // Parse redirects
  let redirectsList = [];
  if (redirectResult?.redirects) {
    try {
      redirectsList = typeof redirectResult.redirects === "string" ? JSON.parse(redirectResult.redirects) : redirectResult.redirects;
    } catch {
      redirectsList = [];
    }
  }

  let redirectWarnings = [];
  if (redirectResult?.warnings) {
    redirectWarnings = parseList(redirectResult.warnings);
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { minHeight: 500 } }}>
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <BuildIcon color="primary" />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Technical SEO Tools
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <Divider />

      <DialogContent>
        {/* Tabs */}
        <Tabs value={activeTab} onChange={handleTabChange} variant="fullWidth" sx={{ borderBottom: 1, borderColor: "divider" }}>
          <Tab icon={<DescriptionIcon />} iconPosition="start" label="robots.txt" />
          <Tab icon={<AccountTreeIcon />} iconPosition="start" label="Sitemap" />
          <Tab icon={<RedoIcon />} iconPosition="start" label="Redirects" />
        </Tabs>

        {/* ========== TAB 0: robots.txt ========== */}
        {activeTab === 0 && (
          <TabPanel>
            <Button
              variant="contained"
              onClick={handleGenerateRobots}
              disabled={robotsLoading || !sessionId}
              startIcon={robotsLoading ? <CircularProgress size={16} color="inherit" /> : <DescriptionIcon />}
              sx={{ mb: 2 }}
            >
              {robotsLoading ? "Generating..." : "Generate robots.txt"}
            </Button>

            {!robotsResult && !robotsLoading && (
              <PlaceholderBox>
                <DescriptionIcon sx={{ fontSize: 36, opacity: 0.4, mb: 1 }} />
                <Typography variant="body2">Click "Generate" to create an optimized robots.txt for your site.</Typography>
              </PlaceholderBox>
            )}

            {robotsResult && (
              <>
                <Box sx={{ position: "relative", mb: 2 }}>
                  <CodeBlock>
                    <code>{robotsResult.content}</code>
                  </CodeBlock>
                  <CopyButton size="small" onClick={() => copyToClipboard(robotsResult.content)}>
                    <ContentCopyIcon fontSize="small" />
                  </CopyButton>
                </Box>

                {parseList(robotsResult.recommendations).length > 0 && (
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                      Recommendations:
                    </Typography>
                    <List dense>
                      {parseList(robotsResult.recommendations).map((rec, idx) => (
                        <ListItem key={idx} disableGutters>
                          <ListItemIcon sx={{ minWidth: 32 }}>
                            <CheckCircleOutlineIcon fontSize="small" color="primary" />
                          </ListItemIcon>
                          <ListItemText
                            primary={rec}
                            primaryTypographyProps={{
                              variant: "body2",
                            }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                )}
              </>
            )}
          </TabPanel>
        )}

        {/* ========== TAB 1: Sitemap ========== */}
        {activeTab === 1 && (
          <TabPanel>
            <Button
              variant="contained"
              onClick={handleGenerateSitemap}
              disabled={sitemapLoading || !sessionId}
              startIcon={sitemapLoading ? <CircularProgress size={16} color="inherit" /> : <AccountTreeIcon />}
              sx={{ mb: 2 }}
            >
              {sitemapLoading ? "Generating..." : "Generate Sitemap"}
            </Button>

            {!sitemapResult && !sitemapLoading && (
              <PlaceholderBox>
                <AccountTreeIcon sx={{ fontSize: 36, opacity: 0.4, mb: 1 }} />
                <Typography variant="body2">Click "Generate" to create an XML sitemap for your site.</Typography>
              </PlaceholderBox>
            )}

            {sitemapResult && (
              <>
                {/* Page count */}
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Pages found:</strong> {sitemapResult.pageCount || 0}
                </Typography>

                {/* Recommendations */}
                {parseList(sitemapResult.recommendations).length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                      Recommendations:
                    </Typography>
                    <List dense>
                      {parseList(sitemapResult.recommendations).map((rec, idx) => (
                        <ListItem key={idx} disableGutters>
                          <ListItemIcon sx={{ minWidth: 32 }}>
                            <CheckCircleOutlineIcon fontSize="small" color="primary" />
                          </ListItemIcon>
                          <ListItemText primary={rec} primaryTypographyProps={{ variant: "body2" }} />
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                )}

                {/* XML output (truncated) */}
                {sitemapResult.xml && (
                  <Box sx={{ position: "relative" }}>
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: "block" }}>
                      XML Preview (truncated to 3000 chars):
                    </Typography>
                    <CodeBlock>
                      <code>{sitemapResult.xml.length > 3000 ? sitemapResult.xml.substring(0, 3000) + "\n..." : sitemapResult.xml}</code>
                    </CodeBlock>
                    <CopyButton size="small" onClick={() => copyToClipboard(sitemapResult.xml)}>
                      <ContentCopyIcon fontSize="small" />
                    </CopyButton>
                  </Box>
                )}
              </>
            )}
          </TabPanel>
        )}

        {/* ========== TAB 2: Redirects ========== */}
        {activeTab === 2 && (
          <TabPanel>
            <Box
              sx={{
                display: "flex",
                gap: 2,
                mb: 2,
                flexWrap: "wrap",
                alignItems: "flex-end",
              }}
            >
              <TextField
                label="URL Pattern (source)"
                placeholder="/old-path/*"
                value={urlPattern}
                onChange={e => setUrlPattern(e.target.value)}
                size="small"
                sx={{ flex: 1, minWidth: 200 }}
              />
              <TextField
                label="New Pattern (destination)"
                placeholder="/new-path/$1"
                value={newPattern}
                onChange={e => setNewPattern(e.target.value)}
                size="small"
                sx={{ flex: 1, minWidth: 200 }}
              />
              <Button
                variant="contained"
                onClick={handleGenerateRedirects}
                disabled={redirectLoading || !sessionId}
                startIcon={redirectLoading ? <CircularProgress size={16} color="inherit" /> : <RedoIcon />}
              >
                {redirectLoading ? "Generating..." : "Generate"}
              </Button>
            </Box>

            {!redirectResult && !redirectLoading && (
              <PlaceholderBox>
                <RedoIcon sx={{ fontSize: 36, opacity: 0.4, mb: 1 }} />
                <Typography variant="body2">Enter URL patterns and click "Generate" to create a redirect map.</Typography>
              </PlaceholderBox>
            )}

            {redirectResult && (
              <>
                {/* Total count */}
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Total redirects:</strong> {redirectResult.totalRedirects || 0}
                </Typography>

                {/* Warnings */}
                {redirectWarnings.length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    {redirectWarnings.map((w, idx) => (
                      <Box
                        key={idx}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          mb: 0.5,
                        }}
                      >
                        <WarningAmberIcon fontSize="small" color="warning" />
                        <Typography variant="body2" color="warning.main">
                          {w}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                )}

                {/* Redirects table */}
                {Array.isArray(redirectsList) && redirectsList.length > 0 && (
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600 }}>Source</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Destination</TableCell>
                          <TableCell sx={{ fontWeight: 600 }} align="center">
                            Status Code
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {redirectsList.map((r, idx) => (
                          <TableRow key={idx} hover>
                            <TableCell>
                              <Typography variant="body2" sx={{ fontFamily: "monospace", fontSize: "0.75rem" }}>
                                {r.source || r.from || r.old}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" sx={{ fontFamily: "monospace", fontSize: "0.75rem" }}>
                                {r.destination || r.to || r.new}
                              </Typography>
                            </TableCell>
                            <TableCell align="center">{r.statusCode || r.status || 301}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </>
            )}
          </TabPanel>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} variant="outlined">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TechnicalSeoTools;
